import { useState } from "react";
import axios from "axios";
import { usePageMeta } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", architect: "", location: "", about: "", website: "" };

export default function Contact() {
  usePageMeta(
    "Contact Sandquist Construction — Berkeley Custom Home Builder",
    "Tell us about your project. Sandquist Construction, 1172 San Pablo Avenue, Suite 200C, Berkeley, CA 94706. (510) 604-1250. CA License #572537."
  );

  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError("Please include your name and email.");
      return;
    }
    setError("");
    setStatus("submitting");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong — please try again, or call (510) 604-1250.");
    }
  };

  const inputClass =
    "w-full bg-canvas border border-hairline px-4 py-3 text-base text-ink placeholder:text-inkmuted/60 focus:outline-none focus:border-plaster transition-colors duration-200";

  return (
    <main className="max-w-[1200px] mx-auto px-6 lg:px-8" data-testid="contact-page">
      <div className="pt-16 sm:pt-24 pb-16 sm:pb-24">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">Tell us about your project.</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-12 mt-14">
          <div className="lg:col-span-7">
            {status === "success" ? (
              <div className="border-t border-plaster pt-10" data-testid="contact-success-message">
                <p className="font-display text-3xl text-ink">Thanks — Rick will be in touch.</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form">
                <div className="space-y-8">
                  <div>
                    <label htmlFor="name" className="block font-label text-xs uppercase tracking-[0.18em] text-inkmuted mb-2">Name</label>
                    <input id="name" type="text" value={form.name} onChange={set("name")} className={inputClass} data-testid="contact-name-input" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label htmlFor="email" className="block font-label text-xs uppercase tracking-[0.18em] text-inkmuted mb-2">Email</label>
                      <input id="email" type="email" value={form.email} onChange={set("email")} className={inputClass} data-testid="contact-email-input" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block font-label text-xs uppercase tracking-[0.18em] text-inkmuted mb-2">Phone</label>
                      <input id="phone" type="tel" value={form.phone} onChange={set("phone")} className={inputClass} data-testid="contact-phone-input" />
                    </div>
                  </div>
                  <fieldset>
                    <legend className="font-label text-xs uppercase tracking-[0.18em] text-inkmuted mb-3">Are you working with an architect?</legend>
                    <div className="flex gap-8">
                      {["Yes", "Not yet"].map((option) => (
                        <label key={option} className="flex items-center gap-2 text-base text-ink cursor-pointer">
                          <input
                            type="radio"
                            name="architect"
                            value={option}
                            checked={form.architect === option}
                            onChange={set("architect")}
                            className="accent-[#77879B] w-4 h-4"
                            data-testid={`contact-architect-${option === "Yes" ? "yes" : "not-yet"}`}
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div>
                    <label htmlFor="location" className="block font-label text-xs uppercase tracking-[0.18em] text-inkmuted mb-2">Where is the project?</label>
                    <input id="location" type="text" value={form.location} onChange={set("location")} className={inputClass} data-testid="contact-location-input" />
                  </div>
                  <div>
                    <label htmlFor="about" className="block font-label text-xs uppercase tracking-[0.18em] text-inkmuted mb-2">About the project</label>
                    <textarea id="about" rows={6} value={form.about} onChange={set("about")} placeholder="Please include a short summary of your project scope and location." className={inputClass} data-testid="contact-about-input" />
                  </div>
                  <div className="hp-field" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input id="website" type="text" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off" />
                  </div>
                  {error && (
                    <p className="text-sm text-plaster" data-testid="contact-error-message" role="alert">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    data-testid="contact-submit-btn"
                    className="font-label border border-ink px-8 py-3 text-xs uppercase tracking-[0.18em] text-ink transition-colors duration-200 hover:bg-ink hover:text-canvas disabled:opacity-50"
                  >
                    {status === "submitting" ? "Sending…" : "Send"}
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="lg:col-span-5 lg:col-start-8" data-testid="contact-sidebar">
            <div className="border-t border-hairline pt-8 space-y-2">
              <p className="font-display text-lg text-ink">Sandquist Construction</p>
              <p className="text-sm text-inkmuted">1172 San Pablo Avenue, Suite 200C</p>
              <p className="text-sm text-inkmuted">Berkeley, CA 94706</p>
              <p className="text-sm text-inkmuted pt-3">
                <a href="tel:+15106041250" className="text-plaster hover:text-ink transition-colors duration-200" data-testid="contact-phone-link">
                  (510) 604-1250
                </a>
              </p>
              <p className="text-sm text-inkmuted pt-3">CA License #572537</p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
